import api from "../api/api";
const BASE_URL = import.meta.env.VITE_APP_API_URL;

export const whatZapService = {
  /**
   * Verificar status da instância WhatsApp
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Resposta da API
   */
  verificarStatusWhatsapp: async (params) => {
    try {
      const url = `${BASE_URL}/verificar-status-evolution`;
      const response = await api.post(url, params);
      return response;
    } catch (error) {
      console.error("Erro ao verificar status:", error);
      throw new Error("Erro ao verificar status do WhatsApp.");
    }
  },

  /**
   * Verificar todas as instâncias disponíveis
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Resposta da API
   */
  verificarInstanciasGeral: async (params) => {
    try {
      const url = `${BASE_URL}/instancias-geral-evolution`;
      const response = await api.post(url, params);
      return response;
    } catch (error) {
      console.error("Erro ao consultar instâncias:", error);
      throw new Error("Erro ao consultar instâncias.");
    }
  },

  /**
   * Desconectar instância WhatsApp
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Resposta da API
   */
  desconectarWhatsapp: async (params) => {
    try {
      const url = `${BASE_URL}/whatsapp/desconectar`;
      const response = await api.post(url, params);
      return response;
    } catch (error) {
      console.error("Erro ao desconectar WhatsApp:", error);
      throw new Error("Erro ao desconectar WhatsApp.");
    }
  },

  /**
   * @deprecated Use desconectarWhatsapp() ao invés
   * Método mantido para compatibilidade, mas redireciona para desconectar
   */
  instanciaReiniciar: async (params) => {
    console.warn("instanciaReiniciar() está deprecated. Use desconectarWhatsapp()");
    return await this.desconectarWhatsapp(params);
  },

  /**
   * Gerar QR Code para conectar WhatsApp
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Resposta da API com QR Code
   */
  gerarQrcode: async (params) => {
    try {
      const url = `${BASE_URL}/evolution-gerar-qrcode`;
      const response = await api.post(url, params);
      return response;
    } catch (error) {
      console.error("Erro ao gerar QR Code:", error);
      throw new Error("Erro ao gerar QR Code.");
    }
  },

  /**
   * Enviar mensagem via WhatsApp
   * @param {Object} params - Dados da mensagem
   * @param {string} params.mensagem - Texto da mensagem
   * @param {string} params.telefone - Número do telefone
   * @param {number} params.empresa_id - ID da empresa
   * @returns {Promise<Object>} - Resposta da API
   */
  enviarWhatsapp: async (params) => {
    try {
      const url = `${BASE_URL}/enviar-mensagem-solution-eduardo`;
      
      // Garantindo que os parâmetros estejam no formato correto
      const dadosEnvio = {
        texto: params.mensagem || params.texto,
        telefone: params.telefone,
        empresa_id: params.empresa_id || 1
      };

      // Validação básica
      if (!dadosEnvio.texto || !dadosEnvio.telefone) {
        throw new Error("Texto e telefone são obrigatórios");
      }

      const response = await api.post(url, dadosEnvio);
      return response;
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      throw error; // ✅ Propagar o erro original para melhor tratamento
    }
  },

  /**
   * Conectar instância WhatsApp (alias para gerarQrcode)
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Resposta da API
   */
  conectarWhatsapp: async (params) => {
    return await this.gerarQrcode(params);
  },

  /**
   * Verificar se WhatsApp está conectado
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<boolean>} - True se conectado
   */
  isWhatsappConectado: async (params) => {
    try {
      const response = await this.verificarStatusWhatsapp(params);
      const status = response?.data?.response?.instance?.state;
      return status === 'open';
    } catch (error) {
      console.error("Erro ao verificar conexão:", error);
      return false;
    }
  },

  /**
   * Obter informações completas da instância
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Dados da instância
   */
  getInfoInstancia: async (params) => {
    try {
      const response = await this.verificarStatusWhatsapp(params);
      return {
        nome: response?.data?.response?.instance?.instanceName || 'N/A',
        status: response?.data?.response?.instance?.state || 'unknown',
        conectado: response?.data?.response?.instance?.state === 'open',
        dadosCompletos: response?.data
      };
    } catch (error) {
      console.error("Erro ao obter info da instância:", error);
      return {
        nome: 'Erro',
        status: 'error',
        conectado: false,
        erro: error.message
      };
    }
  },

  // ✅ NOVOS MÉTODOS ADICIONADOS

  /**
   * Mostrar erro de conexão com mensagens específicas
   * @param {string} status - Status da instância (close, connecting, qr, etc.)
   * @param {string} instancia - Nome da instância
   * @returns {Object} - Configuração da mensagem de erro
   */
  getConfigErroConexao: (status, instancia) => {
    const mensagens = {
      'close': {
        titulo: '📱 WhatsApp Desconectado',
        texto: `A instância "${instancia}" está desconectada. É necessário reconectar antes de enviar mensagens.`,
        botao: 'Ir para Configurações',
        icone: 'error',
        cor: '#f44336'
      },
      'connecting': {
        titulo: '🔄 WhatsApp Conectando',
        texto: `A instância "${instancia}" está tentando conectar. Aguarde alguns momentos.`,
        botao: 'Tentar Novamente',
        icone: 'warning',
        cor: '#ff9800'
      },
      'qr': {
        titulo: '📷 QR Code Pendente',
        texto: `A instância "${instancia}" está aguardando leitura do QR Code.`,
        botao: 'Ver QR Code',
        icone: 'info',
        cor: '#2196f3'
      },
      'unknown': {
        titulo: '❓ Status Desconhecido',
        texto: `A instância "${instancia}" está em estado desconhecido: ${status}`,
        botao: 'Verificar Configurações',
        icone: 'warning',
        cor: '#9e9e9e'
      }
    };

    return mensagens[status] || mensagens['unknown'];
  },

  /**
   * Analisar resultados de envio e gerar relatório
   * @param {Array} resultados - Array com resultados dos envios
   * @returns {Object} - Relatório consolidado
   */
  analisarResultadosEnvio: (resultados) => {
    const sucessos = resultados.filter(r => r.status.includes('✅')).length;
    const erros = resultados.filter(r => r.status.includes('❌')).length;
    const total = resultados.length;

    const pessoasComErro = resultados
      .filter(r => r.status.includes('❌'))
      .map(r => ({ nome: r.pessoa, erro: r.detalhes }));

    const pessoasComSucesso = resultados
      .filter(r => r.status.includes('✅'))
      .map(r => r.pessoa);

    return {
      total,
      sucessos,
      erros,
      percentualSucesso: total > 0 ? Math.round((sucessos / total) * 100) : 0,
      pessoasComErro,
      pessoasComSucesso,
      temErros: erros > 0,
      todosComSucesso: erros === 0 && sucessos > 0,
      todosFalharam: sucessos === 0 && erros > 0
    };
  },

  /**
   * Validar dados antes do envio
   * @param {Object} dados - Dados para validação
   * @returns {Object} - Resultado da validação
   */
  validarDadosEnvio: (dados) => {
    const erros = [];

    if (!dados.texto || dados.texto.trim() === '') {
      erros.push('Texto da mensagem é obrigatório');
    }

    if (!dados.telefone || dados.telefone.trim() === '') {
      erros.push('Telefone é obrigatório');
    }

    if (!dados.empresa_id) {
      erros.push('ID da empresa é obrigatório');
    }

    // Validação básica do telefone (números e alguns caracteres especiais)
    if (dados.telefone && !/^[\d\s\+\-\(\)]+$/.test(dados.telefone)) {
      erros.push('Formato do telefone inválido');
    }

    // Validação do tamanho da mensagem
    if (dados.texto && dados.texto.length > 4096) {
      erros.push('Mensagem muito longa (máximo 4096 caracteres)');
    }

    return {
      valido: erros.length === 0,
      erros
    };
  },

  /**
   * Formatar telefone para envio
   * @param {string} telefone - Número do telefone
   * @returns {string} - Telefone formatado
   */
  formatarTelefone: (telefone) => {
    if (!telefone) return '';
    
    // Remove todos os caracteres não numéricos
    let numeroLimpo = telefone.replace(/\D/g, '');
    
    // Se não começar com 55 (código do Brasil), adiciona
    if (!numeroLimpo.startsWith('55')) {
      numeroLimpo = '55' + numeroLimpo;
    }
    
    return numeroLimpo;
  },

  /**
   * Criar delay entre envios
   * @param {number} segundos - Segundos para aguardar
   * @returns {Promise} - Promise que resolve após o delay
   */
  aguardar: (segundos) => {
    return new Promise(resolve => setTimeout(resolve, segundos * 1000));
  },

  /**
   * Verificar saúde da API WhatsApp
   * @returns {Promise<Object>} - Status da API
   */
  verificarSaudeAPI: async () => {
    try {
      const response = await api.get(`${BASE_URL}/health-check`);
      return {
        online: true,
        resposta: response.data
      };
    } catch (error) {
      return {
        online: false,
        erro: error.message
      };
    }
  },

  /**
   * Obter estatísticas de uso
   * @param {Object} params - Parâmetros com empresa_id
   * @returns {Promise<Object>} - Estatísticas de uso
   */
  obterEstatisticas: async (params) => {
    try {
      const url = `${BASE_URL}/whatsapp/estatisticas`;
      const response = await api.post(url, params);
      return response;
    } catch (error) {
      console.error("Erro ao obter estatísticas:", error);
      throw new Error("Erro ao obter estatísticas do WhatsApp.");
    }
  }
};